import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { CapacityPricingWarningsDto } from "../../../models/court/dto/capacity-pricing-warning.dto";
import { CourtDto } from "../../../models/court/dto/court.dto";
import { UpdateCourtInputDto } from "../../../models/court/dto/update-court.dto";
import { GET_COURT_QUERY_KEY } from "../../court-management/hooks/useGetCourt";
import { GET_LOCATION_COURTS_QUERY_KEY } from "../../court-management/hooks/useGetCourts";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";

/**
 * The response is a court PLUS what the save took away (D136): narrowing a
 * court's sports can remove the venue's last price for a size it still offers.
 * The warning is per-response and must never be cached as if it were part of
 * the court — see the strip below.
 */
export type UpdateCourtResponseDto = CourtDto & CapacityPricingWarningsDto;

export const useUpdateCourt = (id: number, onSuccess: (court: UpdateCourtResponseDto) => void, onError?: () => void) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const { selectedLocation } = useSelectedLocation();
    return useMutation({
        mutationKey: ['updateCourt', id],
        mutationFn: (body: CourtDto) => {
            // Filter out large fields to prevent "request entity too large" error
            const { availabilities, location, ...updatePayload } = body;
            return apiClient.fetchApi<UpdateCourtResponseDto, UpdateCourtInputDto>({ method: 'PUT', endpoint: `courts/${id}`, body: updatePayload });
        },
        onSuccess: (data) => {
            // `capacityPricingWarnings` describes THIS save, not the court, so
            // it is stripped before anything is cached. Left in, it would
            // survive in `GET_COURT_QUERY_KEY` and in the court list long after
            // the operator dismissed the toast, and any later reader of the
            // cached court would see a stale loss reported against an edit that
            // never happened.
            const { capacityPricingWarnings, ...court } = data;
            queryClient.setQueryData<CourtDto | undefined>(GET_COURT_QUERY_KEY(court.id), court);
            onSuccess(data);

            // Locally editing the court data in the cache
            queryClient.setQueryData<CourtDto[]>(GET_LOCATION_COURTS_QUERY_KEY(selectedLocation.id), (oldData) => {
                if (!oldData) return [];
                return oldData.map((existing) => (existing.id === court.id ? { ...existing, ...court } : existing));
            });
            // Invalidate the query to ensure the data is fresh
            queryClient.invalidateQueries({ queryKey: GET_LOCATION_COURTS_QUERY_KEY(selectedLocation.id) });
        },
        onError
    });
}